import { useContext, useEffect, useMemo, useState } from 'react';
import { shallowEqual } from 'react-redux';

import { Box, ProgressStepper, Text } from '@zillow/constellation-10';

import { useSelector } from '../../selectors/useSelector';
import accessUtils from '../../utils/accessUtils';
import { getSubStepMap, hasSubStep } from '../../utils/wizardUtils';

import { EXIT_CTA_LABEL } from '../../constants/WizardConstants';
import { STEP_DIRECTION } from './constants';

import { ModularListingContext } from '../../containers/CreateListingPage/context';

import { FreemiumContext } from '../Freemium/context';

import type React from 'react';
import type { PageWizardProps } from '../Wizard/PageWizard';
import type { WizardStepType } from '../Wizard/types';

type WizardStepControllerProps = {
  currentStepKey: string;
  isOrdered?: boolean;
  onGoToStep: (stepKey: string, options?: { stepDirection?: string }) => void;
  progressSteps?: Array<{ key: string; label: string }>;
  steps: Array<WizardStepType>;
  propertyId?: string;
  wizardTitle?: React.ReactNode;
  onExitWizard?: () => void;
  labelAppearanceBreakpoint?: 'md' | 'xl' | 'xxl';
  showPageWizardDesktopViewBreakpoint?: PageWizardProps['showDesktopViewBreakpoint'];
  isBackDisabled?: boolean;
  isNextDisabled?: boolean;
  isWizardComplete?: boolean;
  entryPoint?: string;
  exitCTALabel?: string;
};

const WizardStepController = ({
  currentStepKey,
  isOrdered = true,
  onGoToStep,
  progressSteps = [],
  steps,
  propertyId,
  wizardTitle,
  onExitWizard,
  labelAppearanceBreakpoint = 'md',
  showPageWizardDesktopViewBreakpoint = 'md',
  isBackDisabled = false,
  isNextDisabled = false,
  isWizardComplete = false,
  entryPoint = '',
  exitCTALabel = EXIT_CTA_LABEL.EXIT,
}: WizardStepControllerProps) => {
  const {
    freemiumState: { canAccessTopOfFunnelPremium },
  } = useContext(FreemiumContext);
  const {
    modularListingState: { isInReactivationFlow },
  } = useContext(ModularListingContext);

  const { active, canBeRelisted } = useSelector(
    (state) => ({
      active: state.properties.currentProperty.active,
      canBeRelisted: state.properties.currentProperty.canBeRelisted,
    }),
    shallowEqual,
  );

  useEffect(() => {
    // allow time for CONFIG to update
    setTimeout(() => {
      checkForSubSteps();
    }, 0);
  }, [steps.length]);

  const canAccessRelistingFlow = accessUtils.canAccessRelistingFlow({
    canBeRelisted,
  });

  const [hasSubSteps, setHasSubSteps] = useState(false);
  const [subStepMap, setSubStepMap] = useState<Map<string, Array<WizardStepType>>>();

  const shouldRenderProgressStepper = useMemo(() => {
    if (!isInReactivationFlow) {
      return true;
    } else if (canAccessRelistingFlow) {
      return true;
    }

    return false;
  }, [active, isInReactivationFlow, canAccessRelistingFlow]);

  const firstValidProgressStepKey = progressSteps[0]?.key;
  const firstValidProgressStepIndex = steps.findIndex((step) => step.progressStepKey === firstValidProgressStepKey);

  const firstValidStepKey =
    firstValidProgressStepIndex >= 0 ? steps[firstValidProgressStepIndex]?.stepKey : steps[0]?.stepKey;

  const currentIndex = useMemo(() => {
    const currentStepIndex = steps.findIndex((step) => step.stepKey === currentStepKey);

    return currentStepIndex !== -1 ? currentStepIndex : steps.findIndex((step) => step.stepKey === firstValidStepKey);
  }, [currentStepKey, firstValidStepKey, steps?.length]);

  const currentStep = steps[currentIndex];
  const currentProgressStepKey = currentStep && currentStep.progressStepKey;
  const progressStepIndex = progressSteps.findIndex((progressStep) => progressStep.key === currentProgressStepKey);
  const currentProgressStepLabel = progressSteps[progressStepIndex]?.label;

  const progressSubStepList = subStepMap?.get(currentProgressStepKey) || [];
  const stepList = hasSubSteps ? progressSubStepList : steps;
  const currentStepIndex = stepList.findIndex((step) => step.stepKey === currentStepKey) + 1;

  const checkForSubSteps = () => {
    if (hasSubStep(steps)) {
      setHasSubSteps(true);
      setSubStepMap(getSubStepMap(steps));
    }
  };

  const renderCurrentComponent = () => {
    const CurrentWizardBodyTemplate = currentStep && currentStep.wizardBodyTemplate;
    const totalSteps = hasSubSteps ? progressSubStepList.length : steps.length;

    let { nextStepKey } = currentStep || {};
    let { previousStepKey } = currentStep || {};

    if (isOrdered) {
      const nextStep = steps[currentIndex + 1];
      nextStepKey = nextStep && nextStep.stepKey;
      const previousStep = steps[currentIndex - 1];
      previousStepKey = previousStep && previousStep.stepKey;
    }

    const progressStepperSteps = progressSteps.map((progressStep, index) => ({
      label: progressStep.label,
      ...(isWizardComplete || index < progressStepIndex ? { tone: 'complete' as const } : {}),
    }));

    // Static responsive display styles for each breakpoint (required for Panda CSS build-time generation)
    const mobileDisplayStyles = {
      md: { base: 'block' as const, md: 'none' as const },
      xl: { base: 'block' as const, xl: 'none' as const },
      xxl: { base: 'block' as const, xxl: 'none' as const },
    }[labelAppearanceBreakpoint];

    const labelAppearanceStyles = {
      md: { base: 'none' as const, md: 'all' as const },
      xl: { base: 'none' as const, xl: 'all' as const },
      xxl: { base: 'none' as const, xxl: 'all' as const },
    }[labelAppearanceBreakpoint];

    const progressStepperToRender = shouldRenderProgressStepper
      ? progressSteps.length &&
        !currentStep.hideProgressStepper &&
        (() => (
          <Box
            css={{ backgroundColor: 'bg.neutral', display: 'flex', justifyContent: 'center' }}
            data-testid="ProgressStepperContainer"
          >
            <Box css={{ flex: 1, maxWidth: '100%' }} data-testid="ProgressStepperContent">
              <ProgressStepper
                activeStepIndex={progressStepIndex}
                steps={progressStepperSteps}
                labelAppearance={labelAppearanceStyles}
                aria-label="Wizard progress"
              />
              {/* Mobile step count display - hidden at desktop breakpoint */}
              <Box
                css={{
                  display: mobileDisplayStyles,
                  marginLeft: '400',
                }}
              >
                <Text textStyle="body-sm-bold">{`${currentProgressStepLabel}`}</Text>
                {currentProgressStepLabel && hasSubSteps && (
                  <Text textStyle="body-sm">{`(${currentStepIndex}/${totalSteps})`}</Text>
                )}
              </Box>
            </Box>
          </Box>
        ))()
      : '';

    return (
      <CurrentWizardBodyTemplate
        entryPoint={entryPoint}
        exitCTALabel={exitCTALabel}
        onGoToStep={onGoToStep}
        onGoToNextStep={nextStepKey ? () => onGoToStep(nextStepKey, { stepDirection: STEP_DIRECTION.NEXT }) : null}
        onGoToPrevStep={
          previousStepKey ? () => onGoToStep(previousStepKey, { stepDirection: STEP_DIRECTION.PREVIOUS }) : null
        }
        hasPrevStep={!!previousStepKey}
        hasNextStep={!!nextStepKey}
        propertyId={propertyId}
        wizardTitle={wizardTitle}
        totalSteps={totalSteps}
        currentStepIndex={currentStepIndex}
        onExitWizard={onExitWizard}
        showDesktopViewBreakpoint={showPageWizardDesktopViewBreakpoint}
        displayPremiumTopOfFunnelHeader={canAccessTopOfFunnelPremium}
        progressStepper={progressStepperToRender}
        currentStepKey={currentStepKey}
        isBackDisabled={isBackDisabled}
        isNextDisabled={isNextDisabled}
      />
    );
  };

  return <>{renderCurrentComponent()}</>;
};

export default WizardStepController;
